/**
 * The mandated import site for every routing primitive in `packages/app` — importing
 * `react-router-dom` directly is a Biome `noRestrictedImports` error. Most exports here are
 * pass-throughs; `useNavigate`, `Link`, and `LinkButton` are wrapped to inject the workspace slug.
 */
import {
  matchRoutes,
  type NavigateFunction,
  type NavigateOptions,
  type To,
  useBeforeUnload,
  useBlocker,
  useParams,
  useLocation,
  useMatch as useRRMatch,
  useMatches as useRRMatches,
  useOutletContext,
  useSearchParams,
  Outlet,
  Route,
  Routes,
  createSearchParams,
  type NavigateProps,
  Link as RRLink,
  useNavigate as RRuseNavigate,
  Navigate as RRNavigate,
  type LinkProps as RRLinkProps,
  MemoryRouter,
  generatePath,
  useNavigation,
  useNavigationType,
  type RouteObject as RRRouteObject,
  type LoaderFunction,
  type PathPattern,
  useLoaderData,
  useRevalidator,
  UNSAFE_LocationContext,
  UNSAFE_RouteContext,
} from "react-router-dom";
import {
  createContext,
  forwardRef,
  type MouseEvent,
  useCallback,
  useContext as useReactContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import {
  Link as HTLink,
  LinkButton as HTLinkButton,
  type LinkButtonProps as HTLinkButtonProps,
  type LinkProps as HTLinkProps,
} from "@hightouch/htui";

// Modify paths to include workspace slug when available
const modifyToWithWorkspace = (
  to: To | number,
  root_workspace_slug: string | undefined,
): To | number => {
  if (typeof to === "number") {
    return to;
  }
  if (!root_workspace_slug) {
    return to;
  }
  if (typeof to === "string") {
    if (to.startsWith("/")) {
      return `/${root_workspace_slug}${to}`;
    }
    return to;
  }
  if (to?.pathname?.startsWith("/")) {
    return { ...to, pathname: `/${root_workspace_slug}${to.pathname}` };
  }

  return to;
};

/**
 * Lets a surface take over in-app navigation to some workspace paths, as settings do to open over the page. It gets the
 * path as written, before the workspace slug is added, and returns true when it handled the navigation.
 */
type NavigationInterceptor = (to: string, options?: NavigateOptions) => boolean;

// Undefined above every surface that sets one, where the app-level interceptor applies instead.
const NavigationInterceptorContext = createContext<
  NavigationInterceptor | null | undefined
>(undefined);

// For navigations from above the layout, such as the command palette's, which no surface's context reaches.
let appLevelInterceptor: NavigationInterceptor | null = null;

export const setAppLevelNavigationInterceptor = (
  next: NavigationInterceptor,
) => {
  appLevelInterceptor = next;
};

// Clears only the interceptor its owner set, so a host unmounting after a new one mounted leaves the new one in place.
export const clearAppLevelNavigationInterceptor = (
  owned: NavigationInterceptor,
) => {
  if (appLevelInterceptor === owned) appLevelInterceptor = null;
};

// Only absolute workspace paths are intercepted; relative ones resolve against routes the interceptor can't see.
const workspacePathOf = (to: To): string | null => {
  const path =
    typeof to === "string"
      ? to
      : `${to.pathname ?? ""}${to.search ?? ""}${to.hash ?? ""}`;
  return path.startsWith("/") ? path : null;
};

const useInterceptNavigation = (isAppLevel: boolean) => {
  const intercept = useReactContext(NavigationInterceptorContext);
  return useCallback(
    (to: To, options?: NavigateOptions) => {
      const path = workspacePathOf(to);
      // A `null` context opts out of interception, even for an app-level navigate.
      const active =
        intercept === null
          ? null
          : isAppLevel || intercept === undefined
            ? appLevelInterceptor
            : intercept;
      return Boolean(active && path && active(path, options));
    },
    [intercept, isAppLevel],
  );
};

const useNavigateWithWorkspace = (isAppLevel: boolean) => {
  const navigate = RRuseNavigate();
  const { root_workspace_slug } = useParams<{ root_workspace_slug: string }>();
  const interceptNavigation = useInterceptNavigation(isAppLevel);

  const navigateWithWorkspace: NavigateFunction = useCallback(
    (to: To | number, options: NavigateOptions = {}) => {
      if (typeof to !== "number" && interceptNavigation(to, options)) {
        return;
      }
      const processedTo = modifyToWithWorkspace(to, root_workspace_slug);
      if (typeof processedTo === "number") {
        return navigate(processedTo);
      }
      return navigate(processedTo, options);
    },
    [navigate, root_workspace_slug, interceptNavigation],
  );

  return navigateWithWorkspace;
};

const useNavigate = () => useNavigateWithWorkspace(false);

// For a page's command that runs from above the layout, such as a palette action, so open settings handle it as they do the palette's own.
export const useAppLevelNavigate = () => useNavigateWithWorkspace(true);

const useMatch = (path: string | PathPattern<string>) => {
  const { root_workspace_slug } = useParams<{ root_workspace_slug: string }>();
  // Without a slug (tests, pre-workspace routes) the path stays as given, like useNavigate's.
  const match = useRRMatch(
    root_workspace_slug ? `/${root_workspace_slug}${path}` : path,
  );

  return match;
};

const useMatches = useRRMatches;

const Navigate = ({ to, ...props }: NavigateProps) => {
  const { root_workspace_slug } = useParams<{ root_workspace_slug: string }>();
  return (
    <RRNavigate
      to={modifyToWithWorkspace(to, root_workspace_slug) as To}
      {...props}
    />
  );
};

const useOriginWithWorkspace = () => {
  const { root_workspace_slug } = useParams<{ root_workspace_slug: string }>();
  const origin = window.location.origin;

  if (!root_workspace_slug) {
    return origin;
  }

  return `${origin}/${root_workspace_slug}`;
};

/**
 * Whether `navigate(-steps)` stays in the app. React Router's browser history counts the tab's entries in
 * `history.state.idx`, which a cold link starts at 0 and a replace keeps.
 */
const canGoBack = (steps = 1) => {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof idx === "number" && idx >= steps;
};

const RouterLink = forwardRef<
  HTMLAnchorElement,
  RRLinkProps & { preload?: "mount" | "intent" }
>(({ to, preload, onClick, ...props }, ref) => {
  const { root_workspace_slug } = useParams<{
    root_workspace_slug: string;
  }>();
  const interceptNavigation = useInterceptNavigation(false);

  // Modified clicks, new-tab targets, and full reloads keep the browser's own handling.
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.altKey ||
      event.ctrlKey ||
      event.shiftKey ||
      (props.target && props.target !== "_self") ||
      props.reloadDocument
    ) {
      return;
    }
    if (interceptNavigation(to)) {
      event.preventDefault();
    }
  };

  const prefetch = useCallback(() => {
    const path = typeof to === "string" ? to : to.pathname;
    if (path) {
      // Preload failures are safe to ignore — the actual navigation
      // will retry the import via React.lazy with proper error handling.
      import("src/router/route-preloads")
        .then(({ preloadRoute }) => preloadRoute(path))
        .catch(() => {});
    }
  }, [to]);

  useEffect(() => {
    if (preload === "mount") prefetch();
  }, [preload, prefetch]);

  return (
    <RRLink
      ref={ref}
      to={modifyToWithWorkspace(to, root_workspace_slug) as To}
      {...(preload === "intent" && {
        onMouseEnter: prefetch,
        onFocus: prefetch,
      })}
      {...props}
      onClick={handleClick}
    />
  );
});

RouterLink.displayName = "RouterLink";

type AnchorProps = {
  target?: string;
  rel?: string;
  download?: string;
};

const LinkButton = forwardRef<
  HTMLAnchorElement,
  HTLinkButtonProps & AnchorProps & { state?: NavigateOptions["state"] }
>(({ href, state, ...props }, ref) => {
  // If the button is disabled, we don't want to use the RouterLink,
  // because that will still allow a navigation event to be triggered.
  // So instead fallback to a normal HTUI LinkButton when disabled.
  if (props.isDisabled) {
    return <HTLinkButton href={href} {...props} ref={ref} />;
  }

  return (
    <HTLinkButton
      as={RouterLink}
      // @ts-expect-error RouterLink prop forwarded through polymorphic component
      to={href}
      state={state}
      {...props}
      ref={ref}
    />
  );
});

LinkButton.displayName = "LinkButton";

const Link = forwardRef<
  HTMLAnchorElement,
  HTLinkProps & AnchorProps & { state?: NavigateOptions["state"] }
>(({ href, state, ...props }, ref) => {
  return (
    <HTLink
      as={RouterLink}
      // @ts-expect-error RouterLink prop forwarded through polymorphic component
      to={href}
      state={state}
      ref={ref}
      {...props}
    />
  );
});

Link.displayName = "Link";

/**
 * Use this outlet component when you do not need to pass any context to your children.
 * @returns An outlet component that does not pass any context to its children
 */
const NoContextOutlet = () => {
  return <Outlet />;
};

// TODO(eric.lovett): `createTypedOutlet` defines components and hooks inside a
// function. Every caller runs at module scope, so each outlet gets a stable
// set, but the rule can't verify that. Please consider passing the per-outlet
// symbol and context to module-level components and hooks.
const createTypedOutlet = <T,>(name: string) => {
  const contextTypeSymbol = Symbol();

  // Backs the non-routed `ContextProvider`. Routed children read context off
  // React Router's outlet context; inline children read it here. Consumers
  // prefer this when set, so both paths resolve through the same hooks.
  const DirectContext = createContext<T | undefined>(undefined);

  // Memoize the wrapped context so OutletContext consumers don't see a fresh
  // reference every render. Without this, every render of the OutletProvider
  // (driven by parent re-renders or query refetches) propagates a new
  // OutletContext value to every consumer in the subtree.
  // biome-ignore lint/nursery/noComponentHookFactories: see TODO above
  const OutletProvider = ({ context }: { context: T }) => {
    const wrapped = useMemo(
      () => ({ ...context, __contextOutletType: contextTypeSymbol }),
      [context],
    );
    return <Outlet context={wrapped} />;
  };

  // Provides the context to direct children rather than a routed `<Outlet>`,
  // for mounting an outlet consumer outside of routing.
  // biome-ignore lint/nursery/noComponentHookFactories: see TODO above
  const ContextProvider = ({
    context,
    children,
  }: {
    context: T;
    children: ReactNode;
  }) => (
    <DirectContext.Provider value={context}>{children}</DirectContext.Provider>
  );

  // biome-ignore lint/nursery/noComponentHookFactories: see TODO above
  const useContext = (): T => {
    const direct = useReactContext(DirectContext);
    const outlet = useOutletContext<{ __contextOutletType: symbol } & T>();
    if (direct !== undefined) {
      return direct;
    }
    if (outlet?.__contextOutletType !== contextTypeSymbol) {
      throw new Error(
        `use${name}OutletContext must be used within <${name}Outlet>. ` +
          `If this component intentionally renders outside the provider, use use${name}OutletContextIfAvailable instead.`,
      );
    }
    return outlet;
  };

  // biome-ignore lint/nursery/noComponentHookFactories: see TODO above
  const useOptionalContext = (): T | undefined => {
    const direct = useReactContext(DirectContext);
    const outlet = useOutletContext<{ __contextOutletType: symbol } & T>();
    if (direct !== undefined) {
      return direct;
    }
    if (outlet?.__contextOutletType !== contextTypeSymbol) {
      return undefined;
    }
    return outlet;
  };

  return {
    OutletProvider,
    ContextProvider,
    useContext,
    useOptionalContext,
  } as const;
};

export {
  Route,
  Routes,
  useBeforeUnload,
  useBlocker,
  useSearchParams,
  useParams,
  useLocation,
  matchRoutes,
  NoContextOutlet,
  createSearchParams,
  MemoryRouter,
  generatePath,
  useNavigation,
  useNavigationType,
  useLoaderData,
  useRevalidator,
  createTypedOutlet,
  canGoBack,
  NavigationInterceptorContext,
  // For a router nested inside the app's, such as the settings modal's memory router.
  UNSAFE_LocationContext,
  UNSAFE_RouteContext,

  // Modified for workspace slug
  Link,
  LinkButton,
  RouterLink,
  useNavigate,
  Navigate,
  useMatch,
  useMatches,
  useOriginWithWorkspace,
};

export type {
  NavigationInterceptor,
  To,
  NavigateOptions,
  NavigateFunction,
  RRRouteObject,
  LoaderFunction,
};

// Re-export space-related types for route definitions
export type {
  HTRouteObject as RouteObject,
  SpacesAccess,
} from "src/components/spaces/types";

// Re-export test utilities
export { createMemoryRouter, RouterProvider } from "react-router-dom";
